import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { House, Users, Gear, ShieldCheck, UserGear, SignOut, Tooth, ChartLineUp } from "@phosphor-icons/react";

const itemBase = "flex items-center gap-3 px-4 py-3 rounded-lg transition-colors text-sm font-medium";
const navItems = [
  { to: "/dashboard", label: "Dashboard", icon: ChartLineUp, testId: "nav-dashboard" },
  { to: "/patients", label: "Pazienti", icon: Users, testId: "nav-patients" },
  { to: "/settings", label: "Impostazioni", icon: Gear, testId: "nav-settings", roles: ["superadmin", "admin"] },
  { to: "/users", label: "Utenti", icon: UserGear, testId: "nav-users", roles: ["superadmin"] },
  { to: "/audit-logs", label: "GDPR Log", icon: ShieldCheck, testId: "nav-audit", roles: ["superadmin", "admin"] },
];

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const role = user?.role;
  const visible = navItems.filter((n) => !n.roles || n.roles.includes(role));

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-paper flex">
      {/* Sidebar (desktop) */}
      <aside className="hidden md:flex flex-col w-64 border-r border-[#E6E8E6] bg-white/60 backdrop-blur-sm">
        <div className="px-6 py-7 flex items-center gap-3 border-b border-[#E6E8E6]">
          <div className="w-10 h-10 rounded-lg bg-brand-700 text-white flex items-center justify-center">
            <Tooth size={22} weight="duotone" />
          </div>
          <div>
            <div className="font-heading font-semibold text-[#1C2321] leading-none">Mediadent</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-brand-400 mt-1">Care Platform</div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {visible.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              data-testid={it.testId}
              className={({ isActive }) =>
                `${itemBase} ${isActive ? "bg-brand-100 text-brand-700" : "text-[#56635D] hover:bg-brand-50"}`
              }
            >
              <it.icon size={18} weight="duotone" />
              <span>{it.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-[#E6E8E6]">
          <div className="text-xs text-[#56635D] mb-2 truncate">{user?.email}</div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-brand-400 mb-3">{role}</div>
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-[#EAF0EC] text-brand-700 hover:bg-[#D5E2DB] transition-colors text-sm"
          >
            <SignOut size={16} weight="duotone" /> Esci
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile header */}
        <header className="md:hidden sticky top-0 z-30 glass">
          <div className="flex items-center justify-between px-4 h-14">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-md bg-brand-700 text-white flex items-center justify-center">
                <Tooth size={16} weight="duotone" />
              </div>
              <div className="font-heading font-semibold text-[#1C2321]">Mediadent Care</div>
            </div>
            <button
              onClick={handleLogout}
              data-testid="logout-button-mobile"
              className="p-2 rounded-md text-brand-700 hover:bg-brand-50"
            >
              <SignOut size={18} weight="duotone" />
            </button>
          </div>
        </header>

        <main className="flex-1 px-4 md:px-10 py-6 md:py-10 pb-24 md:pb-10 max-w-7xl w-full mx-auto" data-testid="main-content">
          {children}
        </main>

        {/* Bottom nav (mobile) */}
        <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur-md border-t border-[#E6E8E6]">
          <div className="grid grid-cols-5">
            {visible.slice(0, 5).map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                data-testid={`${it.testId}-mobile`}
                className={({ isActive }) =>
                  `flex flex-col items-center justify-center gap-1 py-3 text-[11px] ${isActive ? "text-brand-700" : "text-[#56635D]"}`
                }
              >
                <it.icon size={20} weight="duotone" />
                <span>{it.label}</span>
              </NavLink>
            ))}
          </div>
        </nav>
      </div>
    </div>
  );
}
